import { ContextType } from 'react';

import { currentUser } from 'js/utils/user';
import { News, SlimPost } from 'js/social/shared/types';
import { FeedContext } from 'js/social/contexts/feed';

const useElementVisibilities = ({
  feedConfig,
  isQuotedNews,
  news,
  postOwner,
  inActivationOverhaul,
}: {
  feedConfig: ContextType<typeof FeedContext>['feedConfig'];
  isQuotedNews: boolean;
  news: News | SlimPost;
  postOwner: {
    id: number | undefined;
    following: boolean;
  };
  inActivationOverhaul?: boolean;
}) => {
  const { page } = feedConfig;
  const { newsType, paperType } = news;

  const isOpenalexPostOwner = postOwner.id == null;
  const isBookmark = newsType === 'paper_update' && paperType === 'bookmark';
  const isQuotedBookmark = isBookmark && isQuotedNews;
  const isHomePageNonFolloweeBookmark =
    isBookmark &&
    page === 'home' &&
    postOwner.id !== currentUser?.id &&
    !postOwner.following;
  const isHashtagPageBookmark = isBookmark && page === 'hashtag';
  const isSinglePostPageBookmark = isBookmark && page === 'single_post';

  const hideAllActions =
    inActivationOverhaul ||
    // For the following A/B tests, the control behavior recommends
    // paper_update but experimental recommends work.
    // Because these tests were started before works had social actions,
    // maintain the behavior of hiding all actions for both types.
    // - wolf_q3_2024_recency_weighted_paper_recs_v2 (SOC-2345)
    // - wolf_q3_2024_guess_the_researcher (SOC-2361)
    ((newsType === 'paper_update' || newsType === 'work') &&
      news.reason?.type === 'recommended' &&
      (!!feedConfig.recencyWeightedTestBucket ||
        !!feedConfig.guessTheResearcherTestBucket));

  const isWork = newsType === 'work';

  // Openalex works do not have a post owner so certain actions are
  // not supported.
  const isOpenalexWork = newsType === 'openalex_work';

  const canSeeRecommendationRatings =
    feedConfig.recommendationRatingsEnabled && !isQuotedNews;

  return {
    // SOC-1990: Hide the header under certain circumstances to make it appear that we are presenting a plain Work.
    // This also disables user actions in the context menu
    // Everywhere: quoted bookmarks; Because we want bookmarks to behave like the underlying work.
    // Home: bookmarkers that you don't follow.
    // Hashtag and single_posts: all bookmarks.
    // Profile: specifically excluded in ticket description
    showAuthor: ![
      isOpenalexPostOwner,
      isQuotedBookmark,
      isHomePageNonFolloweeBookmark,
      isHashtagPageBookmark,
      isSinglePostPageBookmark,
    ].some(Boolean),
    showHeaderMenu: ![hideAllActions, isOpenalexWork].some(Boolean),
    showContent: ![hideAllActions, isOpenalexWork].some(Boolean),
    showLikeAction: !hideAllActions,
    showCommentAction: !hideAllActions,
    showRepostAction: ![hideAllActions, isWork, isOpenalexWork].some(Boolean),
    showRecommendationRatings: canSeeRecommendationRatings,
  };
};

export default useElementVisibilities;
